import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { OriginState, OriginTheme, OriginContextValue } from './types';

const OriginContext = createContext<OriginContextValue | undefined>(undefined);

export const useOrigin = () => {
  const context = useContext(OriginContext);
  if (!context) {
    throw new Error('useOrigin doit être utilisé au sein d\'un OriginProvider');
  }
  return context;
};

interface OriginProviderProps {
  children: ReactNode;
  defaultTheme?: OriginTheme;
}

export const OriginProvider: React.FC<OriginProviderProps> = ({ 
  children, 
  defaultTheme = 'auto' 
}) => {
  const [theme, setTheme] = useState<OriginTheme>(() => {
    // Tenter de récupérer le thème depuis localStorage (userPreferences)
    try {
      const prefs = localStorage.getItem('userPreferences');
      return prefs ? JSON.parse(prefs).theme : defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  const [state, setState] = useState<OriginState>('idle');

  // Appliquer le thème au document
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'auto') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.setAttribute('data-theme', systemTheme);
    } else {
      root.setAttribute('data-theme', theme);
    }
  }, [theme]);

  const value: OriginContextValue = {
    theme,
    setTheme,
    state,
    setState,
    config: {
      blurIntensity: 22,
      filamentThickness: 2,
    },
  };

  return (
    <OriginContext.Provider value={value}>
      {children}
    </OriginContext.Provider>
  );
};
